/* SheetMaskTimer — 15 min visual breathing-light countdown for Thursday sheet mask ritual. */
import React, { useState, useEffect, useRef } from 'react';
import { X, Play, Pause, RotateCcw, Sparkles } from 'lucide-react';

const TOTAL_SECONDS = 15 * 60;

function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = 'sine';
    o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1.2);
    o.start();
    o.stop(ctx.currentTime + 1.4);
  } catch {
    /* silent */
  }
}

export default function SheetMaskTimer({ onClose }) {
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [running, setRunning] = useState(true);
  const [done, setDone] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!running || done) return;
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          setDone(true);
          setRunning(false);
          playChime();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running, done]);

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');
  const progress = ((TOTAL_SECONDS - secondsLeft) / TOTAL_SECONDS) * 100;
  const size = 260;
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progress / 100) * circumference;

  const reset = () => {
    clearInterval(intervalRef.current);
    setSecondsLeft(TOTAL_SECONDS);
    setDone(false);
    setRunning(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="sheet-mask-timer-modal"
    >
      <div
        className="bg-gradient-to-br from-rose-50 via-pink-50 to-fuchsia-50 rounded-3xl p-8 max-w-md w-full relative shadow-2xl border border-rose-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-white/60 hover:bg-white text-slate-500"
          aria-label="Stäng"
          data-testid="sheet-mask-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-center mb-4">
          <div className="text-[11px] uppercase font-semibold tracking-wider text-rose-800/70">Sheet-mask</div>
          <h2 className="text-2xl font-bold text-rose-900" style={{ fontFamily: 'ui-serif, Georgia, serif' }}>
            15 min lugn ✨
          </h2>
          <p className="text-xs text-rose-800/70 mt-1 italic">
            Lägg dig ner. Andas djupt. Låt masken göra sitt.
          </p>
        </div>

        <div className="relative flex items-center justify-center mb-6">
          {/* Breathing pulse background */}
          <div
            className="absolute rounded-full bg-gradient-to-br from-rose-300 to-fuchsia-300 opacity-30"
            style={{
              width: size + 40,
              height: size + 40,
              animation: running && !done ? 'breathePulse 4s ease-in-out infinite' : 'none',
            }}
          />
          <svg width={size} height={size} className="relative">
            <circle cx={size / 2} cy={size / 2} r={radius} stroke="#fce7f3" strokeWidth={stroke} fill="white" />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke="url(#skGrad)"
              strokeWidth={stroke}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', transition: 'stroke-dashoffset 0.9s linear' }}
            />
            <defs>
              <linearGradient id="skGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#f472b6" />
                <stop offset="100%" stopColor="#e879f9" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute text-center">
            <div className="text-5xl font-bold text-rose-900 tabular-nums" style={{ fontFamily: 'ui-monospace, monospace' }}>
              {mm}:{ss}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-rose-700/60 mt-1">
              {done ? 'klar ✨' : running ? 'andas in / andas ut' : 'pausad'}
            </div>
          </div>
        </div>

        {done ? (
          <div className="text-center space-y-3">
            <div className="text-lg font-semibold text-rose-900">
              <Sparkles className="w-4 h-4 inline mr-1" /> Klar! Ta av masken och massera in resten.
            </div>
            <p className="text-xs text-rose-800/70 italic">
              Din hud har fått 15 minuter av bara kärlek 💜
            </p>
            <div className="flex justify-center gap-2 mt-3">
              <button
                onClick={reset}
                data-testid="sheet-mask-restart"
                className="text-xs px-4 py-2 rounded-full bg-white border border-rose-300 text-rose-800 font-semibold hover:bg-rose-50"
              >
                <RotateCcw className="w-3 h-3 inline mr-1" /> Kör igen
              </button>
              <button
                onClick={onClose}
                className="text-xs px-4 py-2 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white font-semibold"
              >
                Klar 💜
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-center gap-2">
            <button
              onClick={() => setRunning((r) => !r)}
              data-testid="sheet-mask-toggle"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white text-sm font-semibold shadow"
            >
              {running ? <><Pause className="w-4 h-4" /> Pausa</> : <><Play className="w-4 h-4" /> Fortsätt</>}
            </button>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-rose-200 text-rose-800 text-sm hover:bg-rose-50"
              aria-label="Nollställ"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        )}

        <p className="text-[10px] text-center text-rose-700/50 italic mt-4">
          Perfekt för torsdagens hydra-mask-ritual 🌸
        </p>

        <style>{`
          @keyframes breathePulse {
            0%, 100% { transform: scale(1); opacity: 0.25; }
            50% { transform: scale(1.08); opacity: 0.4; }
          }
        `}</style>
      </div>
    </div>
  );
}
